import { ChangeDetectionStrategy, Component, Input, OnInit, TrackByFunction } from '@angular/core';
import { LISTS_PATH, SAVED_RECIPES_PATH } from '@woolworthsnz/shop';
import {
	ButtonComponent,
	DatalayerService,
	NotificationType,
	slugify,
	SvgIconComponent,
} from '@woolworthsnz/styleguide';
import { RecipeCategoryResponse } from '@woolworthsnz/trader-api';
import { BehaviorSubject, take } from 'rxjs';
import { RecipeService } from '../../services';
import { RouterLink } from '@angular/router';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

interface CategoryChip extends Partial<RecipeCategoryResponse> {
	link: string;
}

@Component({
	selector: 'recipe-category-nav',
	template: `
		<!-- Top level ngIf since we don't want an empty <nav> tag on a page -->
		<nav *ngIf="(categories$ | async)?.length" class="recipesNav" aria-label="Category navigation">
			<ul class="recipesNav-categories">
				<li *ngFor="let category of categories$ | async; trackBy: trackByFn">
					<a
						cdxButton
						fillStyle="secondaryOutline"
						[inline]="true"
						[chip]="true"
						[routerLink]="category.link"
						(click)="handleCategoryClick(category.description)"
					>
						{{ category.description }}
					</a>
				</li>
				<li *ngIf="categoryId === 0">
					<a
						cdxButton
						fillStyle="secondaryOutline"
						[inline]="true"
						[chip]="true"
						routerLink="/recipes/0/all-recipes"
						(click)="handleCategoryClick('allrecipes')"
					>
						All Recipes
					</a>
				</li>
				<li *ngIf="categoryId === 0">
					<a
						cdxButton
						fillStyle="primary"
						[inline]="true"
						[chip]="true"
						[routerLink]="recipesListPath"
						[startSlotActive]="true"
						(click)="handleCategoryClick('savedrecipes')"
					>
						<cdx-svg-icon start name="bookmark-circle-a" fill="light"></cdx-svg-icon>
						Saved Recipes
					</a>
				</li>
			</ul>
		</nav>
	`,
	styleUrls: ['./category-nav.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [NgIf, NgFor, ButtonComponent, RouterLink, SvgIconComponent, AsyncPipe],
})
export class CategoryNavComponent implements OnInit {
	@Input() categoryId = 0;

	recipesListPath = `${LISTS_PATH}/${SAVED_RECIPES_PATH}`;

	categories$ = new BehaviorSubject<CategoryChip[]>([]);

	constructor(private recipeService: RecipeService, private datalayerService: DatalayerService) {}

	ngOnInit(): void {
		this.recipeService
			.getCategoryById(this.categoryId)
			.pipe(take(1))
			.subscribe((res) => {
				// Hide categories with no recipes
				const categories =
					res.subcategories
						?.filter((cat) => cat.totalRecipes)
						.map((cat) => this.mapCategoryToCategoryChip(res, cat)) || [];
				this.categories$.next(categories);
			});
	}
	trackByFn: TrackByFunction<CategoryChip> = (_, category) => category.id;

	mapCategoryToCategoryChip(category: RecipeCategoryResponse, subCategory: RecipeCategoryResponse): CategoryChip {
		return {
			...subCategory,
			link:
				this.categoryId === 0
					? `/recipes/${subCategory.id}/${slugify(subCategory.description)}`
					: `/recipes/${category.id}/${slugify(category.description)}/${subCategory.id}/${slugify(
							subCategory.description
					  )}`,
		};
	}

	handleCategoryClick(category = ''): void {
		if (category) {
			const slugifiedCategory = slugify(category);
			this.datalayerService.trackRecipeEvent(NotificationType.Button, `RNAV_${slugifiedCategory}`);
		}
	}
}
